import { useId } from "react";
import { Blossom, Leaf } from "./Flora";
import { between, createRng, round } from "./rng";

const SIZE = 460;
const C = SIZE / 2;
const R_PHOTO = 150;

// Two strands of pearls, each packed edge to edge with no gaps: a large inner
// strand sitting on the photo's edge, and a finer outer strand around it.
const STRANDS = [
  { r: 7.2, ring: R_PHOTO + 5 },
  { r: 4.2, ring: R_PHOTO + 5 + 7.2 + 4.2 + 0.6 },
].map(({ r, ring }) => {
  const count = Math.floor((2 * Math.PI * ring) / (2 * r));
  return { r, ring, count };
});
const OUTER = STRANDS[1].ring + STRANDS[1].r;

const polar = (r, deg) => [C + r * Math.cos((deg * Math.PI) / 180), C + r * Math.sin((deg * Math.PI) / 180)];

/** Bougainvillea clustered along the pearls between two angles (0° = right, 90° = bottom). */
const garland = (seed, from, to, count, delayStart) => {
  const rng = createRng(seed);
  const leaves = [];
  const blooms = [];
  for (let i = 0; i < count; i += 1) {
    const t = count === 1 ? 0.5 : i / (count - 1);
    const taper = 0.55 + 0.45 * Math.sin(Math.PI * t);
    const angle = from + (to - from) * t + between(rng, -2.5, 2.5);
    // Two staggered rows: one over the pearls, one just outside them.
    const row = i % 2 === 0 ? between(rng, -6, 6) : between(rng, 12, 24);
    const [x, y] = polar(OUTER - 4 + row, angle);
    blooms.push({
      x,
      y,
      size: between(rng, 11, 17) * taper,
      angle: between(rng, 0, 120),
      gradient: Math.floor(rng() * 4),
      delay: delayStart + t * 0.8,
    });
    if (rng() > 0.2) {
      const [lx, ly] = polar(OUTER + between(rng, 14, 30), angle + between(rng, -5, 5));
      leaves.push({
        x: lx,
        y: ly,
        length: between(rng, 16, 28) * taper,
        angle: angle + between(rng, -50, 50),
        fill: rng() > 0.5 ? "url(#rm-leaf)" : "url(#rm-leaf-dark)",
      });
    }
  }
  return { leaves, blooms };
};

const GARLANDS = [
  garland(7, 96, 250, 30, 0.6), // the full sweep up the left and over the bottom
  garland(19, 292, 352, 11, 1.2), // a spray at the top right
  garland(31, 18, 58, 7, 1.4), // a small cluster at the lower right
];

/**
 * Round portrait framed only by pearls and pink bougainvillea, the same
 * flowers used across the invitation; the ring turns slowly around the photo. `mirror` swaps the garland to the other
 * side so the bride's and groom's frames face each other.
 */
const PearlFrame = ({ src, alt = "", mirror = false, className = "" }) => {
  const clipId = `rm-pearl-${useId().replace(/[^a-zA-Z0-9-]/g, "")}`;
  return (
    <svg className={`rm-pearl-frame ${className}`} viewBox={`0 0 ${SIZE} ${SIZE}`} role="img" aria-label={alt}>
      <defs>
        <clipPath id={clipId}>
          <circle cx={C} cy={C} r={R_PHOTO + 2} />
        </clipPath>
      </defs>

      <circle cx={C} cy={C} r={R_PHOTO + 2} fill="#e9d8b8" />
      <image
        href={src}
        x={C - R_PHOTO - 2}
        y={C - R_PHOTO - 2}
        width={(R_PHOTO + 2) * 2}
        height={(R_PHOTO + 2) * 2}
        preserveAspectRatio="xMidYMid slice"
        clipPath={`url(#${clipId})`}
      />

      {/* Pearls and flowers turn slowly around the photo; the inner strand the other way. */}
      {STRANDS.map(({ r, ring, count }, s) => (
        <g key={s} className={`rm-pearl-frame__ring ${s === 0 ? "rm-pearl-frame__ring--reverse" : ""}`}>
          {Array.from({ length: count }, (_, i) => {
            const [x, y] = polar(ring, (360 / count) * i - 90);
            return (
              <g key={i}>
                <circle cx={round(x + r * 0.12)} cy={round(y + r * 0.22)} r={r} fill="#6b4a2a" opacity="0.16" />
                <circle cx={round(x)} cy={round(y)} r={r} fill="url(#rm-pearl)" />
                <circle cx={round(x - r * 0.32)} cy={round(y - r * 0.36)} r={round(r * 0.28)} fill="#fff" opacity="0.95" />
              </g>
            );
          })}
        </g>
      ))}

      <g className="rm-pearl-frame__ring">
        <g transform={mirror ? `translate(${SIZE} 0) scale(-1 1)` : undefined}>
          {GARLANDS.map(({ leaves, blooms }, g) => (
            <g key={g}>
              {leaves.map((leaf, i) => (
                <Leaf key={`l${i}`} {...leaf} />
              ))}
              {blooms.map((bloom, i) => (
                <Blossom key={`b${i}`} {...bloom} />
              ))}
            </g>
          ))}
        </g>
      </g>
    </svg>
  );
};

export default PearlFrame;
